// Application bootstrap: local fonts, persisted queries, routing and native chrome.
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import App from './App';
import { ConfirmHost } from './components/ui/ConfirmDialog';

// Locally bundled Latin font preserves the approved numeric design offline;
// Chinese falls back to native sans. IBM Plex Mono remains for code/date uses.
import './styles/fonts.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';

import './index.css';
import './lib/i18n';
import { initNative } from './lib/native';

void initNative();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30,
      gcTime: 1000 * 60 * 60 * 24,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'spent-tracker-cache',
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24 * 7,
        // Bust cache on every deploy — __APP_BUILD_TIME__ is regenerated each
        // `npm run build` (vite.config.ts define). Without this, stale shapes
        // from a previous deploy hydrate into new code and can crash render
        // (e.g. Phase A added net_savings to /insights/summary; old cache
        // hydrated → undefined.income → white screen).
        buster: __APP_BUILD_TIME__,
      }}
    >
      <BrowserRouter>
        <App />
        <ConfirmHost />
      </BrowserRouter>
    </PersistQueryClientProvider>
  </StrictMode>,
);
